// Pure helpers for the Marketing-Influenced Funnel card (no React, no I/O).

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Fixed channels, in legend / stacking order. `match` is the normalised backend name.
export const FIXED_CHANNELS = [
  { label: 'Non_marketing', match: 'nonmarketing', color: '#58dbcb' },
  { label: 'Direct', match: 'direct', color: '#f5a05a' },
  { label: 'Others', match: 'others', color: '#a97af7' },
  { label: 'Organic', match: 'organic', color: '#67a3f7' },
  { label: 'Referral', match: 'referral', color: '#42c1fb' },
  { label: 'Paid social', match: 'paidsocial', color: '#67d9fb' },
  { label: 'Organic search', match: 'organicsearch', color: '#9aeafc' },
];

// Channels the backend sends that are not in the fixed list get a neutral colour.
const FALLBACK_COLORS = ['#9aa5a0', '#b9c2be', '#7d8a85', '#cfd6d3', '#6b7771'];

const squash = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, '');
const isNullChannel = (raw) => raw === null || raw === undefined || raw === '' || raw === 'null';

/** Display label for a backend channel name. null / "null" are shown as "Others". */
export function channelLabel(raw) {
  if (isNullChannel(raw)) return 'Others';
  const fixed = FIXED_CHANNELS.find((c) => c.match === squash(raw));
  if (fixed) return fixed.label;
  if (squash(raw) === 'sdroutbound') return 'SDR Outbound';
  const s = String(raw);
  return s.slice(0, 1).toUpperCase() + s.slice(1).toLowerCase();
}

/** "2026-10-03T00:00:00Z" -> month index (year * 12 + month0). Null when unparsable. */
export function monthIndexOf(dateStr) {
  const m = /^(\d{4})-(\d{2})/.exec(String(dateStr || ''));
  if (!m) return null;
  return Number(m[1]) * 12 + (Number(m[2]) - 1);
}

export const monthIndexOfDate = (d) => d.getFullYear() * 12 + d.getMonth();
const yearOf = (mi) => Math.floor(mi / 12);
const monthOf = (mi) => mi % 12;
const yy = (mi) => String(yearOf(mi)).slice(2);

export const monthLabel = (mi) => `${MONTHS[monthOf(mi)]}, ${yy(mi)}`;
export const quarterLabel = (mi) => `Q${Math.floor(monthOf(mi) / 3) + 1}, ${yy(mi)}`;
export const shortLabel = (label) => String(label).split(',')[0];

const pad = (n) => (n < 10 ? '0' + n : '' + n);
const daysInMonth = (mi) => new Date(yearOf(mi), monthOf(mi) + 1, 0).getDate();

/**
 * Collapses the API response into per-month, per-channel totals.
 * Returns { monthly: { [mi]: { count: {label: n}, value: {label: n} } }, channels: [{label,color,raw[]}] }
 */
export function aggregate(msg) {
  const monthly = {};
  const rawByLabel = {};
  (Array.isArray(msg) ? msg : []).forEach((rec) => {
    const infl = rec && rec.opportunity_marketing_influenced;
    if (!infl) return;
    const mi = monthIndexOf(rec.opportunityCreatedAt);
    if (mi === null) return;
    const credits = infl['Influence by Channels'] || {};
    const revenue = infl['Revenue by Channels'] || {};
    const slot = monthly[mi] || (monthly[mi] = { count: {}, value: {} });
    Object.keys(credits).forEach((raw) => {
      const label = channelLabel(raw);
      const raws = rawByLabel[label] || (rawByLabel[label] = []);
      if (raws.indexOf(raw) < 0) raws.push(raw);
      slot.count[label] = (slot.count[label] || 0) + (Number(credits[raw]) || 0);
      const rev = parseInt(revenue[raw], 10);
      slot.value[label] = (slot.value[label] || 0) + (Number.isFinite(rev) ? rev : 0);
    });
  });

  const channels = FIXED_CHANNELS.map((c) => ({ label: c.label, color: c.color, raw: rawByLabel[c.label] || [] }));
  Object.keys(rawByLabel)
    .filter((l) => !FIXED_CHANNELS.some((c) => c.label === l))
    .sort()
    .forEach((label, i) => {
      channels.push({ label, color: FALLBACK_COLORS[i % FALLBACK_COLORS.length], raw: rawByLabel[label] });
    });
  return { monthly, channels };
}

/** Raw channel names from a poMarketingInfluenced `influencedChannels` value (array, { channel: credit } or { icp: [channels] }). */
function poChannelsOf(infl, icp) {
  if (!infl) return [];
  if (typeof infl === 'string') {
    try { return poChannelsOf(JSON.parse(infl), icp); } catch (e) { return []; }
  }
  if (Array.isArray(infl)) return infl.filter((c) => typeof c === 'string' || c === null);
  if (typeof infl !== 'object') return [];
  const nested = infl['Influence by Channels'];
  if (nested && typeof nested === 'object') return Object.keys(nested);
  const keys = Object.keys(infl);
  const arrays = keys.filter((k) => Array.isArray(infl[k]));
  if (arrays.length) {
    const own = icp && icp !== 'ALL_ICPS' && Array.isArray(infl[icp]) ? [icp] : arrays;
    return own.flatMap((k) => infl[k]);
  }
  return keys.filter((k) => Number(infl[k]) > 0);
}

/**
 * Collapses the potential-opportunity response ({ accountId, influencedChannels, createdAt } rows) into the
 * same { monthly, channels } shape as aggregate(), plus `records` for the Accounts Hub link.
 * Each account is worth 1 in total, shared equally by the channels that influenced it.
 */
export function aggregatePo(msg, icp) {
  const monthly = {};
  const records = [];
  const rawByLabel = {};
  (Array.isArray(msg) ? msg : []).forEach((rec) => {
    const mi = monthIndexOf(rec && rec.createdAt);
    if (mi === null) return;
    const raws = poChannelsOf(rec.influencedChannels, icp);
    if (!raws.length) return;
    const labels = [];
    raws.forEach((raw) => {
      const label = channelLabel(raw);
      const list = rawByLabel[label] || (rawByLabel[label] = []);
      if (list.indexOf(raw) < 0) list.push(raw);
      if (labels.indexOf(label) < 0) labels.push(label);
    });
    const slot = monthly[mi] || (monthly[mi] = { count: {}, value: {} });
    labels.forEach((l) => { slot.count[l] = (slot.count[l] || 0) + 1 / labels.length; });
    records.push({ accountId: rec.accountId, mi, labels, createdAt: rec.createdAt });
  });
  return { monthly, rawByLabel, records };
}

/** Fixed channels first, then any other label seen in either dataset (opportunity channels keep their order). */
export function mergeChannels(channels, rawByLabel) {
  const out = channels.map((c) => ({ ...c, raw: [...c.raw] }));
  Object.keys(rawByLabel).forEach((label) => {
    const hit = out.find((c) => c.label === label);
    if (hit) {
      rawByLabel[label].forEach((r) => { if (hit.raw.indexOf(r) < 0) hit.raw.push(r); });
    } else {
      out.push({ label, color: FALLBACK_COLORS[(out.length - FIXED_CHANNELS.length) % FALLBACK_COLORS.length], raw: [...rawByLabel[label]] });
    }
  });
  return out;
}

/** Per-channel values for a set of months. count = rounded up (credits are fractional), value = whole dollars. */
export function valuesFor(monthly, channels, months, measure, hidden) {
  const key = measure === 'value' ? 'value' : 'count';
  return channels.map((ch, i) => {
    if (hidden && hidden[i]) return 0;
    let sum = 0;
    months.forEach((mi) => {
      sum += (monthly[mi] && monthly[mi][key][ch.label]) || 0;
    });
    sum = Math.round(sum * 1e6) / 1e6;
    return key === 'count' ? Math.ceil(sum) : Math.round(sum);
  });
}

export const sum = (a) => a.reduce((x, y) => x + y, 0);

/** Buckets the month range [a, b] by month or by calendar quarter. */
export function periodsFor(a, b, gran) {
  const out = [];
  for (let mi = a; mi <= b; mi++) {
    if (gran === 'month') {
      out.push({ label: monthLabel(mi), months: [mi] });
    } else {
      const q = quarterLabel(mi);
      const last = out[out.length - 1];
      if (last && last.label === q) last.months.push(mi);
      else out.push({ label: q, months: [mi] });
    }
  }
  return out;
}

/** The period before periods[idx]: the previous bucket, or (monthly only) the month before the range. */
export function previousPeriod(periods, idx, gran) {
  if (idx === null || idx === undefined) return null;
  if (idx > 0) return { label: periods[idx - 1].label, months: periods[idx - 1].months };
  const first = periods[0].months[0];
  if (gran === 'month') return { label: monthLabel(first - 1), months: [first - 1] };
  return null;
}

export function delta(cur, prev) {
  if (prev === 0) return cur === 0 ? { t: '– 0%', c: 'flat' } : { t: 'new', c: 'flat' };
  const d = (cur - prev) / prev;
  const pc = Math.round(Math.abs(d) * 100);
  if (pc === 0) return { t: '– 0%', c: 'flat' };
  return d > 0 ? { t: '▲ ' + pc + '%', c: 'up' } : { t: '▼ ' + pc + '%', c: 'down' };
}

export const fmt = (n) => Number(n).toLocaleString('en-US');

export function fmtMoney(v) {
  if (v >= 1e9) return '$' + Math.round(v / 1e8) / 10 + 'B';
  if (v >= 1e6) return '$' + Math.round(v / 1e5) / 10 + 'M';
  if (v >= 1e3) return '$' + Math.round(v / 100) / 10 + 'K';
  return '$' + Math.round(v);
}

/** "1 Aug, 26 – 14 Oct, 26" (the last month ends today when it is the current month). */
export function rangeLabel(a, b, today) {
  const endDay = b === monthIndexOfDate(today) ? today.getDate() : daysInMonth(b);
  return `1 ${MONTHS[monthOf(a)]}, ${yy(a)} – ${endDay} ${MONTHS[monthOf(b)]}, ${yy(b)}`;
}

/** YYYY-MM-DD bounds of a period (end is clipped to today). */
export function periodBounds(months, today) {
  const first = months[0];
  const last = months[months.length - 1];
  const endDay = last === monthIndexOfDate(today) ? today.getDate() : daysInMonth(last);
  return {
    start: `${yearOf(first)}-${pad(monthOf(first) + 1)}-01`,
    end: `${yearOf(last)}-${pad(monthOf(last) + 1)}-${pad(endDay)}`,
  };
}

export function presetRanges(nowMi) {
  return [
    { id: '3', label: 'Last 3 months', a: nowMi - 2, b: nowMi },
    { id: '6', label: 'Last 6 months', a: nowMi - 5, b: nowMi },
    { id: '12', label: 'Last 12 months', a: nowMi - 11, b: nowMi },
    { id: 'ytd', label: 'Year to date', a: yearOf(nowMi) * 12, b: nowMi },
  ];
}

export function niceStep(x) {
  const p = Math.pow(10, Math.floor(Math.log(x) / Math.LN10));
  const f = [1, 2, 2.5, 5, 10];
  for (let i = 0; i < f.length; i++) if (f[i] * p >= x) return f[i] * p;
  return 10 * p;
}
